/** Role assigned to each TB column by the user. "ignore" = column is unused. */
export type ColumnRole =
  | "ignore"
  | "entity"
  | "account_number"
  | "account_name"
  | "split"
  | "group1"
  | "group2"
  | "group3"
  | "first_period";

/** FDD template defaults — col A=Entity, B=Acct#, C=Name, D=IS/BS, E-G=Groups, H=First Period. */
const DEFAULT_ROLES_BY_INDEX: ColumnRole[] = [
  "entity", "account_number", "account_name", "split", "group1", "group2", "group3", "first_period",
];

/** Parse "YYYY-MM-DD" / "Mon-YY" / "Mon YYYY" header text into (year, month).
 *  Mirrors the server's parsePeriodYM. Returns null if unparseable. */
export function parsePeriodYM(label: string): { year: number; month: number } | null {
  const s = String(label || "").trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return { year: parseInt(iso[1], 10), month: parseInt(iso[2], 10) };
  const named = s.match(/([A-Za-z]{3,9})[\s\-/.]?(\d{2,4})/);
  if (named) {
    const monNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const monNorm = named[1].slice(0, 1).toUpperCase() + named[1].slice(1, 3).toLowerCase();
    const mIdx = monNames.indexOf(monNorm);
    if (mIdx >= 0) {
      let y = parseInt(named[2], 10);
      if (y < 100) y += 2000;
      return { year: y, month: mIdx + 1 };
    }
  }
  return null;
}

/** Header text → label role, matched on lowercase alphanumerics ("#" reads as "no"). */
const LABEL_PATTERNS: [Exclude<ColumnRole, "ignore" | "first_period" | "group2" | "group3">, RegExp][] = [
  ["entity", /^(legal)?(entity|company|subsidiary)(name|code|id)?$/],
  ["account_number", /^(gl|glaccount|account|acct)(no|number|num|code|id)?$/],
  ["account_name", /^(gl|glaccount|account|acct)?(name|description|desc)$/],
  ["split", /^(isbs|bsis|statement|fstype|financialstatement)$/],
  ["group1", /^(category|mapping|map|grouping|group|level|fsli)\d*$/],
];

const isPeriodHeader = (h: string): boolean => {
  const s = h.trim();
  if (parsePeriodYM(s)) return true;
  if (/^(fy|ltm|ytd)\s*'?\d{2,4}$/i.test(s)) return true;
  // Excel serial date that arrived as a number (20000 ≈ 1954, 80000 ≈ 2119).
  const n = Number(s);
  return Number.isInteger(n) && n >= 20000 && n <= 80000;
};

/**
 * Default role per column. Infers from header text when it recognises an
 * account column and a first period; otherwise falls back to the FDD
 * template's positional layout, with the server's date-header guess
 * (`firstPeriodGuess`, -1 = none) placing "first_period".
 */
export function defaultColumnRoles(headers: string[], firstPeriodGuess: number): ColumnRole[] {
  const norm = headers.map((h) => String(h ?? "").toLowerCase().replace(/#/g, "no").replace(/[^a-z0-9]/g, ""));
  const inferred: ColumnRole[] = headers.map(() => "ignore");
  const taken = new Set<ColumnRole>();
  const groups: ColumnRole[] = ["group1", "group2", "group3"];
  let firstPeriod = -1;
  for (let i = 0; i < headers.length && firstPeriod < 0; i++) {
    const hit = LABEL_PATTERNS.find(([role, re]) => re.test(norm[i]) && (role === "group1" || !taken.has(role)));
    if (hit) {
      const role = hit[0] === "group1" ? groups.shift() : hit[0];
      if (role) { inferred[i] = role; taken.add(role); }
    } else if (isPeriodHeader(String(headers[i] ?? ""))) {
      firstPeriod = i;
    }
  }
  if (firstPeriod < 0 && firstPeriodGuess >= 0 && firstPeriodGuess < headers.length && inferred[firstPeriodGuess] === "ignore") {
    firstPeriod = firstPeriodGuess;
  }
  if (firstPeriod >= 0 && (taken.has("account_number") || taken.has("account_name"))) {
    for (let i = firstPeriod + 1; i < inferred.length; i++) inferred[i] = "ignore";
    inferred[firstPeriod] = "first_period";
    return inferred;
  }

  // Positional fallback. If the guess landed past the 7 label slots, columns
  // between the last label and the guessed period stay "ignore".
  return headers.map((_h, i) => {
    if (firstPeriodGuess >= 0 && i === firstPeriodGuess) return "first_period";
    if (firstPeriodGuess >= 0 && i < 7) return DEFAULT_ROLES_BY_INDEX[i] ?? "ignore";
    if (firstPeriodGuess < 0) return DEFAULT_ROLES_BY_INDEX[i] ?? "ignore";
    return "ignore";
  });
}
